"use client";

import { cn } from "@ui/lib";
import type * as React from "react";
import { createContext, useContext, useId, useMemo } from "react";
import * as RechartsPrimitive from "recharts";
import type { VerticalAlignmentType } from "recharts/types/component/DefaultLegendContent";
import type {
	NameType,
	ValueType,
} from "recharts/types/component/DefaultTooltipContent";

// The shadcn chart wrapper. Shadcnblocks and shadcn chart blocks import these
// names, so keep the surface (ChartContainer / ChartConfig / ChartTooltip /
// ChartTooltipContent / ChartLegend / ChartLegendContent) stable.
//
// Note this is NOT a stock `shadcn add chart` drop, and re-running the CLI over
// this file will regress two things:
//   1. The registry build still ships Tailwind v3 arbitrary-property syntax
//      (`border-[--color-border]`, `outline-none`), which silently no-ops on
//      the v4 setup this repo uses. Classes here are v4
//      (`border-(--color-border)`, `outline-hidden`).
//   2. Its tooltip/legend prop types predate Recharts 3, which moved
//      payload/label/verticalAlign out of TooltipProps/LegendProps and into
//      context. The types below are the v3 ones.
// It also overwrites card.tsx, which is customised in this repo.

const THEMES = { light: "", dark: ".dark" } as const;

export type ChartConfig = {
	[k in string]: {
		label?: React.ReactNode;
		icon?: React.ComponentType;
	} & (
		| { color?: string; theme?: never }
		| { color?: never; theme: Record<keyof typeof THEMES, string> }
	);
};

type ChartContextProps = { config: ChartConfig };

const ChartContext = createContext<ChartContextProps | null>(null);

function useChart() {
	const context = useContext(ChartContext);
	if (!context) {
		throw new Error("useChart must be used within a <ChartContainer />");
	}
	return context;
}

function ChartContainer({
	id,
	className,
	children,
	config,
	...props
}: React.ComponentProps<"div"> & {
	config: ChartConfig;
	children: React.ComponentProps<
		typeof RechartsPrimitive.ResponsiveContainer
	>["children"];
}) {
	const uniqueId = useId();
	const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

	return (
		<ChartContext.Provider value={{ config }}>
			<div
				data-slot="chart"
				data-chart={chartId}
				className={cn(
					"flex aspect-video justify-center text-xs",
					// recessive grid & axes — the data is the ink, the frame is not
					"[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground",
					"[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50",
					"[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border",
					"[&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border",
					"[&_.recharts-radial-bar-background-sector]:fill-muted",
					"[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted",
					"[&_.recharts-reference-line_[stroke='#ccc']]:stroke-border",
					"[&_.recharts-dot[stroke='#fff']]:stroke-transparent",
					"[&_.recharts-layer]:outline-hidden",
					"[&_.recharts-sector]:outline-hidden",
					"[&_.recharts-sector[stroke='#fff']]:stroke-transparent",
					"[&_.recharts-surface]:outline-hidden",
					className,
				)}
				{...props}
			>
				<ChartStyle id={chartId} config={config} />
				<RechartsPrimitive.ResponsiveContainer>
					{children}
				</RechartsPrimitive.ResponsiveContainer>
			</div>
		</ChartContext.Provider>
	);
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
	const colorConfig = Object.entries(config).filter(
		([, cfg]) => cfg.theme || cfg.color,
	);

	if (!colorConfig.length) {
		return null;
	}

	// The content is built entirely from `id` (generated) and the config's own
	// colour tokens — no user input reaches it. This is the only way to emit
	// per-chart CSS custom properties for both themes.
	return (
		<style
			dangerouslySetInnerHTML={{
				__html: Object.entries(THEMES)
					.map(
						([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
	.map(([key, itemConfig]) => {
		const color =
			itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
			itemConfig.color;
		return color ? `  --color-${key}: ${color};` : null;
	})
	.filter(Boolean)
	.join("\n")}
}
`,
					)
					.join("\n"),
			}}
		/>
	);
};

const ChartTooltip = RechartsPrimitive.Tooltip;

function ChartTooltipContent({
	active,
	payload,
	className,
	indicator = "dot",
	hideLabel = false,
	hideIndicator = false,
	label,
	labelFormatter,
	labelClassName,
	formatter,
	color,
	nameKey,
	labelKey,
}: Partial<RechartsPrimitive.TooltipContentProps<ValueType, NameType>> &
	Omit<React.ComponentProps<"div">, "color"> & {
		hideLabel?: boolean;
		hideIndicator?: boolean;
		indicator?: "line" | "dot" | "dashed";
		nameKey?: string;
		labelKey?: string;
		/** Overrides the indicator swatch colour for every row. */
		color?: string;
	}) {
	const { config } = useChart();

	const tooltipLabel = useMemo(() => {
		if (hideLabel || !payload?.length) {
			return null;
		}

		const [item] = payload;
		const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
		const itemConfig = getPayloadConfigFromPayload(config, item, key);
		const value =
			!labelKey && typeof label === "string"
				? config[label as keyof typeof config]?.label || label
				: itemConfig?.label;

		if (labelFormatter) {
			return (
				<div className={cn("font-medium", labelClassName)}>
					{labelFormatter(value, payload)}
				</div>
			);
		}

		if (!value) {
			return null;
		}

		return <div className={cn("font-medium", labelClassName)}>{value}</div>;
	}, [
		label,
		labelFormatter,
		payload,
		hideLabel,
		labelClassName,
		config,
		labelKey,
	]);

	if (!active || !payload?.length) {
		return null;
	}

	const nestLabel = payload.length === 1 && indicator !== "dot";

	return (
		<div
			className={cn(
				"border-border/50 bg-background grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl",
				className,
			)}
		>
			{!nestLabel ? tooltipLabel : null}
			<div className="grid gap-1.5">
				{/* v3 includes hidden entries in the payload — drop them */}
				{payload
					.filter((item) => item.type !== "none")
					.map((item, index) => {
						const key = `${nameKey || item.name || item.dataKey || "value"}`;
						const itemConfig = getPayloadConfigFromPayload(
							config,
							item,
							key,
						);
						const indicatorColor =
							color || item.payload?.fill || item.color;

						return (
							<div
								// dataKey may be an accessor function in v3, so it can't be a key
								key={`${typeof item.dataKey === "string" || typeof item.dataKey === "number" ? item.dataKey : (item.name ?? index)}`}
								className={cn(
									"[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5",
									indicator === "dot" && "items-center",
								)}
							>
								{formatter &&
								item?.value !== undefined &&
								item.name ? (
									formatter(
										item.value,
										item.name,
										item,
										index,
										item.payload,
									)
								) : (
									<>
										{itemConfig?.icon ? (
											<itemConfig.icon />
										) : (
											!hideIndicator && (
												<div
													className={cn(
														"shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
														{
															"h-2.5 w-2.5":
																indicator ===
																"dot",
															"w-1":
																indicator ===
																"line",
															"w-0 border-[1.5px] border-dashed bg-transparent":
																indicator ===
																"dashed",
															"my-0.5":
																nestLabel &&
																indicator ===
																	"dashed",
														},
													)}
													style={
														{
															"--color-bg":
																indicatorColor,
															"--color-border":
																indicatorColor,
														} as React.CSSProperties
													}
												/>
											)
										)}
										<div
											className={cn(
												"flex flex-1 justify-between leading-none",
												nestLabel
													? "items-end"
													: "items-center",
											)}
										>
											<div className="grid gap-1.5">
												{nestLabel
													? tooltipLabel
													: null}
												{/* labels wear text tokens, never the series colour */}
												<span className="text-muted-foreground">
													{itemConfig?.label ||
														item.name}
												</span>
											</div>
											{item.value !== undefined && (
												<span className="text-foreground font-mono font-medium tabular-nums">
													{item.value.toLocaleString()}
												</span>
											)}
										</div>
									</>
								)}
							</div>
						);
					})}
			</div>
		</div>
	);
}

const ChartLegend = RechartsPrimitive.Legend;

function ChartLegendContent({
	className,
	hideIcon = false,
	payload,
	verticalAlign = "bottom",
	nameKey,
}: React.ComponentProps<"div"> & {
	// v3 reads payload/verticalAlign from context rather than exposing them on
	// LegendProps, so a custom legend declares them itself.
	payload?: ReadonlyArray<RechartsPrimitive.LegendPayload>;
	verticalAlign?: VerticalAlignmentType;
	hideIcon?: boolean;
	nameKey?: string;
}) {
	const { config } = useChart();

	if (!payload?.length) {
		return null;
	}

	return (
		<div
			className={cn(
				"flex items-center justify-center gap-4",
				verticalAlign === "top" ? "pb-3" : "pt-3",
				className,
			)}
		>
			{payload.map((item) => {
				const key = `${nameKey || item.dataKey || "value"}`;
				const itemConfig = getPayloadConfigFromPayload(
					config,
					item,
					key,
				);

				return (
					<div
						key={String(item.value)}
						className={cn(
							"[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3",
						)}
					>
						{itemConfig?.icon && !hideIcon ? (
							<itemConfig.icon />
						) : (
							<div
								className="h-2 w-2 shrink-0 rounded-[2px]"
								style={{ backgroundColor: item.color }}
							/>
						)}
						{itemConfig?.label}
					</div>
				);
			})}
		</div>
	);
}

/** Resolve a config entry from a recharts payload item. */
function getPayloadConfigFromPayload(
	config: ChartConfig,
	payload: unknown,
	key: string,
) {
	if (typeof payload !== "object" || payload === null) {
		return undefined;
	}

	const payloadPayload =
		"payload" in payload &&
		typeof payload.payload === "object" &&
		payload.payload !== null
			? payload.payload
			: undefined;

	let configLabelKey: string = key;

	if (
		key in payload &&
		typeof payload[key as keyof typeof payload] === "string"
	) {
		configLabelKey = payload[key as keyof typeof payload] as string;
	} else if (
		payloadPayload &&
		key in payloadPayload &&
		typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
	) {
		configLabelKey = payloadPayload[
			key as keyof typeof payloadPayload
		] as string;
	}

	return configLabelKey in config
		? config[configLabelKey]
		: config[key as keyof typeof config];
}

export {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	ChartLegend,
	ChartLegendContent,
	ChartStyle,
};
